import { addDays } from '@respiria/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronRight,
  Cigarette,
  Flame,
  Heart,
  Pill,
  TrendingDown,
  Trophy,
  Wallet,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardTitle } from '@/components/ui/Card';
import { useTour } from '@/features/tour/TourProvider';
import { hasSeenTour, markTourSeen } from '@/features/tour/tour-seen';
import { ProgressBar, ProgressRing } from '@/components/ui/ProgressRing';
import { Skeleton, StatTile } from '@/components/ui/StatTile';
import { ShareButton } from '@/components/ShareButton';
import { CheckinCard } from '@/features/checkin/CheckinCard';
import { useT } from '@/i18n/useT';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useDashboard, useLiveSeconds, type Dashboard } from '@/hooks/useDashboard';
import { sendOrQueue } from '@/lib/offline-queue';
import { invalidateActivity, invalidateDerived, queryKeys } from '@/lib/query-keys';
import { formatDate, formatDuration, formatMoney, formatRelative, formatTime, newClientId, todayLocal } from '@/lib/format';

export function DashboardPage() {
  const t = useT();
  const { auth } = useAuth();
  const { data, isLoading } = useDashboard();
  const { startTour } = useTour();
  const firstName = auth?.user.displayName.split(' ')[0] ?? '';

  // First time in a modality — including right after upgrading to a new one —
  // auto-launch its tutorial once (issue #57).
  const mode = data?.mode;
  useEffect(() => {
    if (!mode || hasSeenTour(mode)) return;
    markTourSeen(mode);
    startTour(mode);
  }, [mode, startTour]);

  if (isLoading || !data) return <DashboardSkeleton />;

  return (
    <div className="space-y-3 py-6">
      <header className="mb-1">
        <p className="text-[13px] font-medium text-muted">
          {firstName ? t('home.greetingName', { name: firstName }) : t('home.greeting')}
        </p>
      </header>

      {data.mode === 'smoking' ? (
        <SmokerHome data={data} />
      ) : data.isReductionPhase ? (
        <ReductionHero data={data} />
      ) : (
        <StreakHero data={data} />
      )}

      <Card>
        <p className="text-[15px] leading-relaxed text-content">{data.motivation.text}</p>
      </Card>

      {data.treatment && <TreatmentCard data={data} />}

      {/* A quien aún fuma se le enseña la puerta de las pastillas (#95); quien ya
          lo dejó la tiene en «Más», sin que el inicio se la proponga. */}
      {!data.treatment && (data.mode === 'smoking' || data.isReductionPhase) && <PillsCard />}

      {data.stage && <StageCard stage={data.stage} />}

      {data.maintenance?.offerDue && <MaintenanceOfferCard days={data.maintenance.daysSince} />}

      {!data.isReductionPhase && data.mode !== 'smoking' && <StatGrid data={data} />}

      <div data-tour="tour-checkin">
        <CheckinCard />
      </div>

      {data.health.next && <HealthCard data={data} />}

      {!data.isReductionPhase && data.mode !== 'smoking' && <MoneyCard data={data} />}

      <LevelCard data={data} />

      <ShareButton />
    </div>
  );
}

/**
 * Home for someone who still smokes and hasn't set anything up (mode 'smoking',
 * issue #34). No streak or savings yet: instead, track today's consumption (the
 * daily habit) and invite them to pick a Quit Day, which moves them into the
 * reduction/quitting flow.
 */
function SmokerHome({ data }: { data: Dashboard }) {
  return (
    <>
      <SmokerConsumptionCard data={data} />
      <SmokerQuitDayCard />
      <SmokerInfoCard />
    </>
  );
}

/** What you gain by quitting — framed as gains, never guilt (regla de oro 1). */
function SmokerInfoCard() {
  const t = useT();
  return (
    <Card>
      <div className="mb-2 flex items-center gap-2">
        <Heart size={18} className="text-brand" aria-hidden />
        <CardTitle>{t('home.smoker.whyTitle')}</CardTitle>
      </div>
      <ul className="space-y-1.5 text-[14px] leading-relaxed text-muted">
        <li>· {t('home.smoker.why1')}</li>
        <li>· {t('home.smoker.why2')}</li>
        <li>· {t('home.smoker.why3')}</li>
      </ul>
      <p className="mt-3 text-[13px] leading-relaxed text-content">{t('home.smoker.measure')}</p>
    </Card>
  );
}

function SmokerConsumptionCard({ data }: { data: Dashboard }) {
  const t = useT();
  const queryClient = useQueryClient();
  const today = data.reduction?.today ?? 0;
  const baseline = data.reduction?.baseline ?? 0;
  const target = data.reduction?.target ?? null;

  const logCigarette = useMutation({
    mutationFn: () => {
      const clientId = newClientId();
      return sendOrQueue(clientId, '/api/smoke-events', 'POST', {
        clientId,
        type: 'cigarette',
        occurredAt: new Date().toISOString(),
        quantity: 1,
      });
    },
    onSuccess: () => invalidateActivity(queryClient),
  });

  return (
    <Card data-tour="tour-hero">
      <div className="flex items-end justify-between">
        <div>
          <p className="tabular text-[40px] font-bold leading-none text-content">{today}</p>
          <p className="mt-1 text-[13px] text-muted">{t('home.smoker.smokedToday')}</p>
        </div>
        {baseline > 0 && (
          <p className="text-right text-[12px] text-muted">{t('home.smoker.baseline', { n: baseline })}</p>
        )}
      </div>

      {target != null && (
        <div className="mt-3">
          <ProgressBar
            value={Math.min(100, (today / Math.max(1, target)) * 100)}
            color={today <= target ? 'var(--brand)' : 'var(--warning)'}
            label={t('home.smoker.goalLabel')}
          />
          <p className="mt-1.5 text-[13px] font-medium" style={{ color: today <= target ? 'var(--brand)' : 'var(--warning)' }}>
            {today <= target ? t('home.smoker.goalOk', { n: target }) : t('home.smoker.goalOver', { n: today, target })}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => logCigarette.mutate()}
        disabled={logCigarette.isPending}
        className="mt-4 flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-surface-2 text-[15px] font-semibold text-content transition-transform active:scale-[0.98] disabled:opacity-50"
      >
        <Cigarette size={17} aria-hidden />
        {t('home.smoker.logCig')}
      </button>

      <GoalEditor target={target} baseline={baseline} />
    </Card>
  );
}

/** Set or change the daily reduction target (issue #34). */
function GoalEditor({ target, baseline }: { target: number | null; baseline: number }) {
  const t = useT();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(target ?? Math.max(1, baseline - 1)));

  const save = useMutation({
    mutationFn: (n: number) =>
      api('/api/settings', { method: 'PATCH', body: JSON.stringify({ reductionTargetPerDay: n }) }),
    onSuccess: () => {
      invalidateDerived(queryClient);
      setEditing(false);
    },
  });

  if (target != null && !editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-2 w-full text-center text-[12px] text-muted underline-offset-2 hover:underline"
      >
        {t('home.smoker.goalSet', { n: target })} · {t('home.smoker.change')}
      </button>
    );
  }

  return (
    <div className="mt-3 border-t border-line pt-3">
      <p className="mb-2 text-[13px] text-muted">{t('home.smoker.goalPrompt')}</p>
      <div className="flex gap-2">
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={baseline || 100}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          aria-label={t('home.smoker.goalLabel')}
          className="tabular h-11 w-20 rounded-[var(--radius-btn)] bg-surface-2 px-3 text-center text-[16px] text-content"
        />
        <button
          type="button"
          onClick={() => save.mutate(Math.max(0, Math.round(Number(value) || 0)))}
          disabled={save.isPending}
          className="flex min-h-11 flex-1 items-center justify-center rounded-[var(--radius-btn)] bg-brand text-[14px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          {save.isPending ? t('common.saving') : t('home.smoker.goalSave')}
        </button>
      </div>
    </div>
  );
}

function SmokerQuitDayCard() {
  const t = useT();
  const queryClient = useQueryClient();
  const today = todayLocal();
  const [date, setDate] = useState('');

  const setQuitDay = useMutation({
    mutationFn: (localDate: string) =>
      api('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          mode: 'quitting',
          quitDate: new Date(`${localDate}T00:00:00`).toISOString(),
        }),
      }),
    onSuccess: () => {
      invalidateDerived(queryClient);
      invalidateActivity(queryClient);
    },
  });

  const presets = [
    { label: t('home.smoker.today'), days: 0 },
    { label: t('home.smoker.week'), days: 7 },
    { label: t('home.smoker.twoWeeks'), days: 14 },
    { label: t('home.smoker.month'), days: 30 },
  ];

  return (
    <Card data-tour="tour-quitday">
      <CardTitle>{t('home.smoker.title')}</CardTitle>
      <p className="mb-4 mt-1 text-[14px] leading-relaxed text-muted">{t('home.smoker.body')}</p>

      <input
        type="date"
        value={date}
        min={today}
        onChange={(e) => setDate(e.target.value)}
        aria-label={t('home.smoker.title')}
        className="mb-3 h-12 w-full rounded-[var(--radius-btn)] border border-line bg-surface px-4 text-[16px] text-content"
      />

      <div className="mb-4 grid grid-cols-4 gap-2">
        {presets.map((preset) => (
          <button
            key={preset.days}
            type="button"
            onClick={() => setDate(addDays(today, preset.days))}
            className="min-h-10 rounded-[var(--radius-pill)] bg-surface-2 px-2 text-[12px] font-medium text-content"
          >
            {preset.label}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => date && setQuitDay.mutate(date)}
        disabled={!date || setQuitDay.isPending}
        className="flex min-h-12 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-brand text-[15px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
      >
        {setQuitDay.isPending ? t('common.saving') : t('home.smoker.cta')}
      </button>
    </Card>
  );
}

function StreakHero({ data }: { data: Dashboard }) {
  const t = useT();
  const seconds = useLiveSeconds(data.streak.currentSeconds);

  return (
    <Card className="text-center" data-tour="tour-hero">
      <p className="text-[13px] font-medium text-muted">{t('home.notSmoking')}</p>
      <p className="tabular my-1 text-[44px] font-bold leading-none tracking-tight text-brand">
        {formatDuration(seconds)}
      </p>
      <div className="mt-3 flex justify-center gap-5 text-[12px] text-muted">
        <span>
          {t('home.bestStreak')}{' '}
          <strong className="text-content">{formatDuration(data.streak.bestSeconds)}</strong>
        </span>
        <span>
          {t('home.cleanDays')} <strong className="text-content">{data.streak.cleanDays}</strong>
        </span>
      </div>
    </Card>
  );
}

/**
 * Before the Día D the hero is a countdown, not a streak — there is no streak
 * to show yet and cigarettes smoked now are part of the plan (ADR-015).
 */
function ReductionHero({ data }: { data: Dashboard }) {
  const t = useT();
  const days = Math.ceil(data.secondsUntilQuitDate / 86_400);
  const reduction = data.reduction;

  return (
    <>
      <Card className="text-center" data-tour="tour-hero">
        <p className="text-[13px] font-medium text-muted">{t('home.quitDay')}</p>
        <p className="tabular my-1 text-[44px] font-bold leading-none tracking-tight text-brand">
          {days} {days === 1 ? t('common.day') : t('common.days')}
        </p>
        {data.quitDate && (
          <p className="text-[13px] text-muted">{formatDate(data.quitDate)}</p>
        )}
      </Card>

      {reduction && (
        <Card>
          <div className="mb-3 flex items-center gap-2">
            <TrendingDown size={18} className="text-brand" aria-hidden />
            <CardTitle>{t('home.goingDown')}</CardTitle>
          </div>

          <div className="flex items-end justify-between">
            <div>
              <p className="tabular text-[32px] font-bold leading-none text-content">
                {reduction.today}
              </p>
              <p className="text-[12px] text-muted">{t('home.cigsToday')}</p>
            </div>
            <div className="text-right text-[12px] text-muted">
              <p>
                {t('home.yesterday')} <strong className="text-content">{reduction.yesterday}</strong>
              </p>
              <p>
                {t('home.before')}{' '}
                <strong className="text-content">
                  {reduction.baseline}
                  {t('home.perDay')}
                </strong>
              </p>
            </div>
          </div>

          <div className="mt-4">
            <ProgressBar
              value={Math.min(100, (reduction.today / Math.max(1, reduction.baseline)) * 100)}
              color="var(--warning)"
              label={t('dash.ariaReduction')}
            />
          </div>

          <p className="mt-3 text-[13px] leading-relaxed text-muted">
            El dinero ahorrado y los días sin fumar empiezan a contar el{' '}
            {data.quitDate ? formatDate(data.quitDate) : t('common.quitDay')}.
          </p>
        </Card>
      )}
    </>
  );
}

function TreatmentCard({ data }: { data: Dashboard }) {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const treatment = data.treatment!;

  // The dashboard takes the dose without a detour through the treatment screen
  // (docs/FEATURES.md#dashboard-inicio).
  const take = useMutation({
    mutationFn: (doseId: number) =>
      sendOrQueue(`dose-${doseId}-take`, `/api/treatment/doses/${doseId}/take`, 'POST', {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.treatment() });
    },
  });

  // The home lets you take the dose in one tap, but only once it is close: a
  // dose still hours away (typically tomorrow's) must not be markable here, or
  // it is trivial to take it early by accident. Window: 2 h before it is due.
  const TAKE_WINDOW_MS = 2 * 60 * 60 * 1000;
  const canTakeNext =
    treatment.nextDose != null &&
    new Date(treatment.nextDose.scheduledAt).getTime() - Date.now() <= TAKE_WINDOW_MS;

  // Terminada la pauta, el home ya no habla de tomas: la puerta pasa a ser la
  // tarjeta de la etapa, que es de todos y no solo de quien tuvo pastillas
  // (#66 la introdujo aquí; #73 la sacó a su propia sección).
  if (treatment.finished) return null;

  return (
    <Card data-tour="tour-treatment">
      <button
        type="button"
        onClick={() => navigate('/tratamiento')}
        className="flex w-full items-center gap-4 text-left"
      >
        <ProgressRing
          value={treatment.progressPct}
          size={86}
          strokeWidth={8}
          label={`Tratamiento al ${treatment.progressPct} por ciento`}
        >
          <span className="tabular text-[20px] font-bold text-content">{treatment.day}</span>
          <span className="text-[10px] text-muted">de {treatment.totalDays}</span>
        </ProgressRing>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Pill size={16} className="text-brand" aria-hidden />
            <CardTitle>{treatment.brand}</CardTitle>
          </div>
          <p className="mt-1 text-[13px] text-muted">
            {t('treatment.remaining', { n: treatment.remainingDoses })}
          </p>
          {treatment.nextDose && (
            <p className="mt-1 text-[13px] font-medium text-content">
              {t('treatment.nextDose', { when: formatRelative(treatment.nextDose.scheduledAt, t) })}
            </p>
          )}
        </div>
      </button>

      {treatment.nextDose && canTakeNext && (
        <button
          type="button"
          onClick={() => take.mutate(treatment.nextDose!.id)}
          disabled={take.isPending}
          className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-brand text-[15px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          <Check size={17} aria-hidden />
          {take.isPending
            ? t('common.saving')
            : t('treatment.taken', { time: formatTime(treatment.nextDose.scheduledAt) })}
        </button>
      )}
    </Card>
  );
}

/** Puerta a la guía de «quiero tomar las pastillas» (#95). */
function PillsCard() {
  const t = useT();
  const navigate = useNavigate();

  return (
    <Card>
      <button
        type="button"
        onClick={() => navigate('/tratamiento')}
        className="flex w-full items-center gap-4 text-left"
      >
        <span
          className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-full bg-surface-2 text-brand"
          aria-hidden
        >
          <Pill size={26} />
        </span>
        <div className="flex-1">
          <CardTitle>{t('home.pills.title')}</CardTitle>
          <p className="mt-1 text-[13px] leading-relaxed text-muted">{t('home.pills.body')}</p>
        </div>
        <ChevronRight size={18} className="shrink-0 text-muted" aria-hidden />
      </button>
    </Card>
  );
}

/**
 * La puerta de la etapa posterior al tratamiento (issue #73).
 *
 * Está en el home a propósito: la mecánica de las situaciones se usa varias
 * veces al día, y enterrada en una subpantalla no la usaba nadie. Se enseña a
 * cualquiera que tenga Día D, con o sin pastillas — lo único que cambia según
 * el caso es la línea de «sin la pastilla».
 */
function StageCard({ stage }: { stage: NonNullable<Dashboard['stage']> }) {
  const t = useT();
  const navigate = useNavigate();

  return (
    <Card>
      <button
        type="button"
        onClick={() => navigate('/etapa')}
        className="flex w-full items-center gap-4 text-left"
      >
        <span
          className="flex h-[62px] w-[62px] shrink-0 items-center justify-center rounded-full bg-surface-2 text-[26px]"
          aria-hidden
        >
          🧭
        </span>

        <div className="flex-1">
          <CardTitle>{t('stage.pageTitle')}</CardTitle>
          {stage.kind === 'post_pills' && stage.unsupported === 'fresh' && (
            <p className="mt-1 text-[13px] text-muted">
              {t('stage.homeWithoutPills', { n: stage.daysSincePills ?? 0 })}
            </p>
          )}
          <p className="mt-1 text-[13px] font-medium text-content">
            {t('stage.homeSituations', { n: stage.mastered, total: stage.situations })}
          </p>
        </div>

        <ChevronRight size={18} className="shrink-0 text-muted" aria-hidden />
      </button>
    </Card>
  );
}

/**
 * La propuesta de pasar a mantenimiento (issue #55).
 *
 * Es una pregunta, no un aviso de que ya ha pasado: quien todavía se vive como
 * «dejándolo» tiene que poder decir que no sin que la app insista al día
 * siguiente. Por eso «Aún no» se guarda, y por eso el texto deja claro que no
 * se pierde nada al aceptar.
 */
function MaintenanceOfferCard({ days }: { days: number }) {
  const t = useT();
  const queryClient = useQueryClient();

  const answer = useMutation({
    mutationFn: (accept: boolean) =>
      api('/api/settings/maintenance', { method: 'POST', body: JSON.stringify({ accept }) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings() });
    },
  });

  return (
    <Card>
      <CardTitle>{t('maintenance.offerTitle')}</CardTitle>
      <p className="mt-1 text-[13px] leading-relaxed text-muted">
        {t('maintenance.offerBody', { n: days })}
      </p>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => answer.mutate(true)}
          disabled={answer.isPending}
          className="flex min-h-11 flex-1 items-center justify-center rounded-[var(--radius-btn)] bg-brand text-[15px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          {answer.isPending ? t('common.saving') : t('maintenance.accept')}
        </button>
        <button
          type="button"
          onClick={() => answer.mutate(false)}
          disabled={answer.isPending}
          className="flex min-h-11 flex-1 items-center justify-center rounded-[var(--radius-btn)] bg-surface-2 text-[15px] font-semibold text-content transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          {t('maintenance.later')}
        </button>
      </div>

      <p className="mt-2 text-[12px] text-muted">{t('maintenance.laterHint')}</p>
    </Card>
  );
}

function StatGrid({ data }: { data: Dashboard }) {
  const t = useT();
  return (
    <div className="grid grid-cols-3 gap-3">
      <StatTile
        icon={<Cigarette size={18} />}
        value={String(data.avoided.total)}
        label={t('home.avoided')}
      />
      <StatTile
        icon={<Wallet size={18} />}
        value={formatMoney(data.savings.totalCents, data.savings.currency)}
        label={t('home.saved')}
        color="var(--money)"
      />
      <StatTile
        icon={<Trophy size={18} />}
        value={String(data.cravings.resistedTotal)}
        label={t('home.cravingsBeaten')}
      />
    </div>
  );
}

function HealthCard({ data }: { data: Dashboard }) {
  const t = useT();
  const navigate = useNavigate();
  const next = data.health.next!;

  return (
    <Card>
      <button type="button" onClick={() => navigate('/salud')} className="w-full text-left">
        <div className="mb-3 flex items-center gap-2">
          <Heart size={18} className="text-brand" aria-hidden />
          <CardTitle>{t('home.recovery')}</CardTitle>
          <span className="ml-auto text-[12px] text-muted">
            {data.health.unlockedCount}/{data.health.totalCount}
          </span>
        </div>

        <ProgressBar value={data.health.progressPct} label={t('dash.ariaHealth')} />

        <div className="mt-3 flex items-center gap-2">
          <span className="text-[18px]" aria-hidden>
            {next.icon}
          </span>
          <div>
            <p className="text-[14px] font-medium text-content">{next.titleEs}</p>
            <p className="text-[12px] text-muted">{formatRelative(next.reachedAt, t)}</p>
          </div>
        </div>
      </button>
    </Card>
  );
}

function LevelCard({ data }: { data: Dashboard }) {
  const t = useT();
  const navigate = useNavigate();

  return (
    <Card>
      <button type="button" onClick={() => navigate('/progreso')} className="w-full text-left">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame size={18} style={{ color: 'var(--xp)' }} aria-hidden />
            <CardTitle>{t('home.level', { n: data.rpg.level })}</CardTitle>
          </div>
          <span className="text-[12px] font-medium text-muted">{data.rpg.title}</span>
        </div>
        <ProgressBar value={data.rpg.progressPct} color="var(--xp)" label={t('dash.ariaLevel')} />
        <p className="mt-2 text-[12px] text-muted">
          {data.rpg.xpInLevel} / {data.rpg.xpForNextLevel} XP
        </p>
      </button>
    </Card>
  );
}

/** The savings tile is the one that most often deserves its own screen. */
function MoneyCard({ data }: { data: Dashboard }) {
  const t = useT();
  const navigate = useNavigate();

  return (
    <Card data-tour="tour-money">
      <button
        type="button"
        onClick={() => navigate('/dinero')}
        className="flex w-full items-center gap-3 text-left"
      >
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
          style={{ background: 'color-mix(in srgb, var(--money) 14%, transparent)' }}
        >
          <Wallet size={20} style={{ color: 'var(--money)' }} aria-hidden />
        </span>
        <div className="flex-1">
          <CardTitle>{t('home.money')}</CardTitle>
          <p className="mt-0.5 text-[13px] text-muted">
            {t('home.savedAmount', {
              money: formatMoney(data.savings.totalCents, data.savings.currency),
            })}
          </p>
        </div>
        <ChevronRight size={18} className="text-muted" aria-hidden />
      </button>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-3 py-6">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-20 w-full" />
      <Skeleton className="h-28 w-full" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    </div>
  );
}
